// @flow
import { get } from "lodash";
import type { WagerProfile } from "@tvg/types/User";
import { formatWroBets } from "@tvg/utils/pastPerformanceUtils";

type GraphProps = {
  runnerName: string,
  entityRunnerId?: string,
  trackCode?: string,
  raceNumber?: string,
  raceDate?: string,
  wagerProfile: WagerProfile,
  fcpClient: mixed,
  behgClient: mixed,
  accountNumber: string,
  behgToggle: boolean
};

const QUERY_VARIABLES_IPP = {
  wagerProfile: "FDR-Generic",
  entityRunnerId: "",
  sortBy: { byPostTime: "DESC" }
};

const QUERY_VARIABLES_PP = {
  wagerProfile: "FDR-Generic",
  trackCode: "",
  raceNumber: "",
  date: ""
};

const getfinishPosition = (finishPosition: ?number, scratched: false) => {
  if (scratched) {
    return 0;
  }

  return finishPosition;
};

const handleResultsIPP = (result: {}) => {
  const raceIds = [];
  return {
    races: get(result, "data.pastRaces", []).map((race) => {
      const tempRace = {
        ...race,
        id: race.raceId,
        hasReplays: !!get(race, "video.replayFileName", ""),
        replayFileName: get(race, "video.replayFileName", ""),
        raceTypeCode: get(race, "type.code"),
        distance: `${get(race, "distance.value", "")}${get(
          race,
          "distance.code",
          ""
        )}`.toLowerCase(),
        currentOdds: get(race, "results.runners[0].currentOdds"),
        finishPosition: getfinishPosition(
          get(race, "results.runners[0].finishPosition"),
          get(race, "results.runners[0].scratched", false)
        )
      };

      raceIds.push(race.raceId);

      delete tempRace.raceId;
      delete tempRace.results;
      delete tempRace.type;
      delete tempRace.video;

      return tempRace;
    }),
    raceIds,
    isLoading: get(result, "data.loading", false)
  };
};

export const ApolloOptionsIPP = {
  skip: (props: GraphProps) =>
    props.entityRunnerId === "" || props.entityRunnerId === undefined,
  options: (props: GraphProps) => {
    const variables = {
      ...QUERY_VARIABLES_IPP,
      wagerProfile: props.wagerProfile,
      entityRunnerId: props.entityRunnerId
    };

    return {
      client: props.fcpClient,
      fetchPolicy: "cache-and-network",
      pollInterval: 0,
      ssr: false,
      variables
    };
  },
  props: handleResultsIPP
};

const mapRunnersToFullResults = (bettingInterests, runners) =>
  (runners || []).map((runner) => {
    const bi = bettingInterests[runner.biNumber - 1];
    const runnerBI = bettingInterests[runner.biNumber - 1].runners.find(
      (r) => r.runnerId === runner.runnerNumber
    );

    const runnerResult = {
      runnerId: runner.runnerNumber,
      horseName: runner.runnerName,
      jockey: runnerBI.jockey,
      trainer: runnerBI.trainer,
      scratched: runnerBI.scratched,
      currentOdds: bi.currentOdds,
      finishPosition: runner.finishPosition
    };

    return runner.timeform
      ? {
          ...runnerResult,
          odds: bi.currentOdds,
          beatenDistance: runner.timeform.beatenDistance,
          postRaceReport: runner.timeform.postRaceReport
        }
      : runnerResult;
  });

const handleResultsPP = (result: {}) => ({
  pastRace: get(result, "data.pastRaces[0]", null),
  fullResults: mapRunnersToFullResults(
    get(result, "data.pastRaces[0].bettingInterests", []),
    get(result, "data.pastRaces[0].results.runners", [])
  ),
  isLoading: get(result, "data.loading", false),
  hasTimeformData: !!get(
    result,
    "data.pastRaces[0].results.runners[0].timeform",
    false
  )
});

export const ApolloOptionsPP = {
  skip: (props: GraphProps) =>
    props.runnerName === "" || props.runnerName === undefined,
  options: (props: GraphProps) => {
    const variables = {
      ...QUERY_VARIABLES_PP,
      wagerProfile: props.wagerProfile,
      trackCode: props.trackCode,
      raceNumber: props.raceNumber,
      date: props.raceDate
    };

    return {
      client: props.fcpClient,
      fetchPolicy: "cache-and-network",
      pollInterval: 0,
      ssr: false,
      variables
    };
  },
  props: handleResultsPP
};

const handleResultsWager = (result: {}) => ({
  raceBets: formatWroBets({
    data: { wagers: get(result, "data.wagerHistory.wagers", []) }
  })
});

export const ApolloOptionsWager = {
  skip: ({ accountNumber, behgToggle }: GraphProps) =>
    !accountNumber || !behgToggle,
  options: ({
    trackCode,
    raceNumber,
    raceDate,
    accountNumber,
    behgClient
  }: GraphProps) => {
    const variables = {
      trackCode,
      raceNumber,
      startDate: raceDate,
      endDate: raceDate,
      accountId: accountNumber
    };

    return {
      client: behgClient,
      fetchPolicy: "cache-and-network",
      pollInterval: 0,
      ssr: false,
      variables
    };
  },
  props: handleResultsWager
};
